import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import { getStudentTrades, getMyStudents } from "wasp/client/operations";
import StudentLayout from "./StudentLayout";
import MentorNoteEditor from "./MentorNoteEditor";
import useTranslation from "../client/hooks/useTranslation";
import { Badge } from "../client/components/ui/badge";
import { MessageSquare } from "lucide-react";
import { format } from "date-fns";

export default function StudentJournalPage() {
  const t = useTranslation();
  const { studentId } = useParams<{ studentId: string }>();
  const [expandedTradeId, setExpandedTradeId] = useState<string | null>(null);

  const { data: students = [], isLoading: loadingStudents } = useQuery(getMyStudents);
  const { data: tradesData, isLoading, refetch } = useQuery(
    getStudentTrades,
    { studentId: studentId ?? "" },
    { enabled: !!studentId },
  );

  if (!studentId) return null;

  const student = students.find((s: any) => s.studentId === studentId);
  const permissions = student?.permissions ?? {
    canViewJournal: false,
    canViewDashboard: false,
    canViewAnalytics: false,
    canViewSessions: false,
    canViewPropFirmManager: false,
  };

  if (loadingStudents) {
    return (
      <StudentLayout
        studentId={studentId}
        studentFirstName={null}
        studentLastName={null}
        permissions={permissions}
      >
        <p className="text-sm text-muted-foreground">Chargement...</p>
      </StudentLayout>
    );
  }

  if (!permissions.canViewJournal) {
    return (
      <StudentLayout
        studentId={studentId}
        studentFirstName={student?.student?.firstName ?? null}
        studentLastName={student?.student?.lastName ?? null}
        studentEmail={student?.student?.email ?? undefined}
        permissions={permissions}
      >
        <p className="text-muted-foreground text-sm">{t.mentor.noPermissionSection}</p>
      </StudentLayout>
    );
  }

  const tradesDataTyped = tradesData as { trades: any[]; total: number } | undefined;
  const trades = tradesDataTyped?.trades ?? [];
  const total = tradesDataTyped?.total ?? 0;

  return (
    <StudentLayout
      studentId={studentId}
      studentFirstName={student?.student?.firstName ?? null}
      studentLastName={student?.student?.lastName ?? null}
      studentEmail={student?.student?.email ?? undefined}
      permissions={permissions}
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-foreground">
            {t.mentor.canViewJournal} — {total} trades
          </h2>
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Chargement...</p>
        ) : trades.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun trade.</p>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-card border-b border-border">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Date</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Symbole</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Type</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">PnL</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Compte</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground">Note</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((trade: any) => {
                  const hasNote = trade.mentorNotes?.length > 0;
                  const pnlNum = trade.pnl ?? 0;
                  const pnlColor = pnlNum > 0 ? "text-pnlPositive" : pnlNum < 0 ? "text-pnlNegative" : "text-muted-foreground";

                  return (
                    <>
                      <tr
                        key={trade.id}
                        className="border-b border-border/50 hover:bg-accent/30 transition-colors cursor-pointer"
                        onClick={() =>
                          setExpandedTradeId(expandedTradeId === trade.id ? null : trade.id)
                        }
                      >
                        <td className="px-3 py-2 font-mono text-xs">
                          {trade.date ? format(new Date(trade.date), "dd/MM/yy") : "—"}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs font-medium">{trade.symbol}</td>
                        <td className="px-3 py-2">
                          <Badge
                            variant="outline"
                            className={`text-xs px-1.5 py-0 ${trade.type === "long" ? "text-primary border-primary/40" : "text-pnlNegative border-pnlNegative/40"}`}
                          >
                            {trade.type}
                          </Badge>
                        </td>
                        <td className={`px-3 py-2 text-right font-mono text-xs font-medium ${pnlColor}`}>
                          {pnlNum >= 0 ? "+" : ""}{pnlNum.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {trade.account?.name ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <MessageSquare
                            className={`h-3.5 w-3.5 mx-auto ${hasNote ? "text-primary" : "text-muted-foreground/40"}`}
                          />
                        </td>
                      </tr>
                      {expandedTradeId === trade.id && (
                        <tr key={`${trade.id}-note`} className="bg-primary/5">
                          <td colSpan={6} className="px-4 py-3">
                            <MentorNoteEditor
                              studentId={studentId}
                              tradeId={trade.id}
                              existingNote={trade.mentorNotes?.[0]}
                              onSaved={() => refetch()}
                            />
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </StudentLayout>
  );
}
